// MyAsksStrip — horizontal scrollable strip of the kid's own active
// food/recipe requests, mounted at the top of the kid Foods tab.
// Status-aware pills:
//   pending   → 💤 "Asked!"      — parent hasn't seen yet
//   accepted  → ✋ "Coming!"     — parent saw it, on the way
//   cooking   → 🔥 "Cooking now" — parent's cook session is live;
//                                  tap routes to the Watch tab so
//                                  the kid sees full step + ETA
//                                  via the existing KidWatchTab.
//
// Rows fade out automatically when status flips to done/dismissed
// (the hook filters them out). Empty state: nothing renders, no
// placeholder — the strip is purely additive.
//
// EMOJI-IN-UI EXCEPTION: kids-mode opt-in.

import { useEffect, useRef } from "react";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";
import { useMyKidRequests } from "../../lib/useMyKidRequests";
import { playKidVoice } from "../../lib/kidVoice";
import { playCanonicalVoiceline, playRecipeVoiceline } from "../../lib/canonicalVoicelines";
import { useKidPetState } from "../../lib/useKidPetState";

const STATE_META = {
  pending:   { emoji: "💤", label: "Asked!",      tint: "rgba(212,166,55,0.18)",  border: "rgba(212,166,55,0.55)" },
  accepted:  { emoji: "✋", label: "Coming!",     tint: "rgba(123,156,92,0.18)",  border: "rgba(123,156,92,0.55)" },
  cooking:   { emoji: "🔥", label: "Cooking now", tint: "rgba(217,107,43,0.22)",  border: "rgba(217,107,43,0.65)" },
  celebrate: { emoji: "🎉", label: "You got it!", tint: "rgba(123,156,92,0.28)",  border: "rgba(123,156,92,0.75)" },
};

export function MyAsksStrip({ userId, onWatch }) {
  const { rows } = useMyKidRequests(userId);
  const { pet } = useKidPetState(userId);

  // Detect pending|accepted → cooking transitions and play the
  // celebratory narration once per request. Keys the seen set on
  // row id so we don't re-trigger if the kid leaves + returns to
  // the Foods tab while the same request is still cooking.
  const cookingAnnouncedRef = useRef(new Set());
  useEffect(() => {
    for (const r of rows) {
      if (r.displayState !== "cooking") continue;
      if (cookingAnnouncedRef.current.has(r.id)) continue;
      cookingAnnouncedRef.current.add(r.id);
      // "<food> is getting made!" — play the canonical/recipe
      // voiceline FIRST (the food name in the pet's voice), then
      // the bundled "is getting made" pet clip. Best-effort; if
      // either is missing the user just hears the other.
      const slug = r.recipe_slug || r.canonical_id;
      const target = r.request_kind === "recipe" ? "recipe" : "canonical";
      const play = target === "recipe" ? playRecipeVoiceline : playCanonicalVoiceline;
      if (slug && pet?.pet_slug) {
        play(slug, pet.pet_slug);
      }
      setTimeout(() => playKidVoice("is-getting-made"), 1100);
    }
  }, [rows, pet?.pet_slug]);

  // "You got it!" — play a happy clip once when a request lands in
  // the celebrate state (parent's cook finished).
  const celebratedRef = useRef(new Set());
  useEffect(() => {
    for (const r of rows) {
      if (r.displayState !== "celebrate") continue;
      if (celebratedRef.current.has(r.id)) continue;
      celebratedRef.current.add(r.id);
      playKidVoice("yum");
    }
  }, [rows]);

  if (!rows || rows.length === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        padding: "8px 12px 12px",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {rows.map((r) => {
        const meta = STATE_META[r.displayState] || STATE_META.pending;
        const isCooking = r.displayState === "cooking";
        const display = r.food_name || r.canonical_id || r.recipe_slug || "Food";
        const tappable = isCooking && typeof onWatch === "function";
        const onTap = () => {
          if (tappable) onWatch(r);
        };
        return (
          <button
            key={r.id}
            type="button"
            onClick={tappable ? onTap : undefined}
            className="mcm-focusable"
            disabled={!tappable}
            style={{
              all: "unset",
              cursor: tappable ? "pointer" : "default",
              flexShrink: 0,
              maxWidth: 260,
              padding: "8px 12px 8px 10px",
              background: meta.tint,
              border: `1.5px solid ${meta.border}`,
              borderRadius: RADIUS.xxl,
              display: "flex",
              alignItems: "center",
              gap: 8,
              touchAction: "manipulation",
            }}
          >
            <span style={{ fontSize: 24, lineHeight: 1 }}>
              {r.food_emoji || meta.emoji}
            </span>
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <span style={{
                fontFamily: FONT.itemTitle,
                fontStyle: "italic",
                fontSize: 14,
                color: COLOR.ink,
                lineHeight: 1.1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 200,
              }}>
                {display}
              </span>
              <span style={{
                fontFamily: FONT.mono,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.10em",
                color: COLOR.ink,
                textTransform: "uppercase",
                marginTop: 1,
              }}>
                {meta.label}{tappable ? " · tap" : ""}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default MyAsksStrip;
